import { FC, Fragment, ReactNode, useEffect, useState } from "react";
import { Combobox, Dialog, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import {
  ExclamationTriangleIcon,
  FolderIcon,
  LifebuoyIcon,
} from "@heroicons/react/24/outline";
import Image from "componentsTemplate/Image";
import { DEMO_AUTHORS } from "data/authors";
import { DEMO_CATEGORIES } from "data/taxonomies";
import { DEMO_POSTS } from "data/posts";
import Link from "componentsTemplate/Link";
import { useNavigate } from "react-router-dom";
import { getSearch } from "services/SearchService";

const categories = DEMO_CATEGORIES.filter((_, i) => i < 5);
// const posts = DEMO_POSTS.filter((_, i) => i < 5);
// const authors = DEMO_AUTHORS.filter((_, i) => i < 9);

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

interface Props {
  renderTrigger?: () => ReactNode;
}

const SearchModal: FC<Props> = ({ renderTrigger }) => {
  const [open, setOpen] = useState(false);
  const [rawQuery, setRawQuery] = useState("");
  const [authors, setAuthors] = useState<any>([]);	
  const [posts, setPosts] = useState<any>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async (searchTerm: string) => {
    try {
      setLoading(true);
      const response = await getSearch({ search: searchTerm });

      if (response['users']) {
        const authorsTmp = response['users'].map((user: any) => {
          return {
            id: user.id,
            firstName: user.first_name,
            lastName: user.last_name,
            displayName: user.display_name || `${user.first_name} ${user.last_name}`,
            avatar: user.avatar_path ? process.env.REACT_APP_BACKEND_API_URL + '/storage/users/' + user.avatar_path : 'https://images.pexels.com/photos/912410/pexels-photo-912410.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260',
            href: `/${user.id}/perfil`,
            bgImage: 'https://images.pexels.com/photos/912410/pexels-photo-912410.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260'
          }
        });
  
        setAuthors(authorsTmp);
      }

      let postsTmp = [];

      if (response['events']) {
        postsTmp = response['events'].map((event: any) => {
          const categoryInfo = event.category ? ` • ${event.category.name}` : '';
          const subcategoryInfo = event.subcategory ? ` • ${event.subcategory.name}` : '';
          return {
            id: event.id,
            title: 'Evento: ' + event.name,
            subtitle: categoryInfo + subcategoryInfo,
            href: `/evento-detalhes/${event.id}`,
          }   
        } 
      )};

      if (response['tips']) {
        postsTmp = postsTmp.concat(
          response['tips'].map((tip: any) => {
            const categoryInfo = tip.category ? ` • ${tip.category.name}` : '';
            const subcategoryInfo = tip.subcategory ? ` • ${tip.subcategory.name}` : '';
            return {
              id: tip.id,
              title: 'Dica: ' + tip.name,
              subtitle: categoryInfo + subcategoryInfo,
              href: `/dicas-detalhes/${tip.id}`,
            }   
          }) 
        );
      }

      if (response['classifieds']) {
        postsTmp = postsTmp.concat(
          response['classifieds'].map((classified: any) => {
            const categoryInfo = classified.category ? ` • ${classified.category.name}` : '';
            const subcategoryInfo = classified.subcategory ? ` • ${classified.subcategory.name}` : '';
            return {
              id: classified.id,
              title: 'Classificado: ' + classified.name,
              subtitle: categoryInfo + subcategoryInfo,
              href: `/classificados/${classified.id}`,
            }   
          }) 
        );
      }

      if (response['partnerships']) {
        postsTmp = postsTmp.concat(
          response['partnerships'].map((partnership: any) => {
            const categoryInfo = partnership.category ? ` • ${partnership.category.name}` : '';
            const subcategoryInfo = partnership.subcategory ? ` • ${partnership.subcategory.name}` : '';
            return {
              id: partnership.id,
              title: 'Parceiro: ' + partnership.name,
              subtitle: categoryInfo + subcategoryInfo,
              href: `/parceiros/${partnership.id}`,
            }   
          }) 
        );
      }

      setPosts(postsTmp);

    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Busca inicial quando o modal abre
  useEffect(() => {
    if (open) {
      fetchData("");
    }
  }, [open]);

  // Busca em tempo real quando o usuário digita
  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(() => {
        fetchData(rawQuery);
      }, 300); // Debounce de 300ms

      return () => clearTimeout(timeoutId);
    }
  }, [rawQuery, open]);

  const router = useNavigate();

  const query = rawQuery.toLowerCase().replace(/^[#>]/, "");

  const filteredPosts =
    rawQuery === "#"
      ? posts
      : query === "" || rawQuery.startsWith(">")
      ? []
      : posts.filter((project: any) => 
          project.title.toLowerCase().includes(query) ||
          (project.subtitle && project.subtitle.toLowerCase().includes(query))
        );

  const filteredProjects =
    rawQuery === "#"
      ? categories
      : query === "" || rawQuery.startsWith(">")
      ? []
      : categories.filter((project) =>
          project.name.toLowerCase().includes(query)
        );

  const filteredUsers =
    rawQuery === ">"
      ? authors
      : query === "" || rawQuery.startsWith("#")
      ? []
      : authors.filter((user: any) =>
          user.displayName.toLowerCase().includes(query)
        );

  return (
    <>
      <div onClick={() => setOpen(true)} className="cursor-pointer">
        {renderTrigger ? (
          renderTrigger()
        ) : (
          <button className="flex w-10 h-10 sm:w-12 sm:h-12 rounded-full text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none items-center justify-center">
            <svg
              width={22}
              height={22}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M22 22L20 20"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      <Transition.Root
        show={open}
        as={Fragment}
        afterLeave={() => setRawQuery("")}
        appear
      >
        <Dialog
          as="div"
          className="relative z-[99]"
          onClose={() => setOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-100"
            >
              <Dialog.Panel
                className="block mx-auto max-w-2xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all"
                as="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  router("/search");
                  setOpen(false);
                }}
              >
                <Combobox
                  onChange={(item: any) => {
                    const newPath = item.href;
                    
                    setOpen(false);
                    
                    // Verifica se a nova rota é do tipo id/perfil
                    const newPathParts = newPath.split('/').filter((p: string) => p);
                    const isPerfilRoute = newPathParts.length === 2 && newPathParts[1] === 'perfil';
                    
                    // Se for rota de perfil, sempre força reload para garantir atualização
                    if (isPerfilRoute) {
                      setTimeout(() => {
                        window.location.href = newPath;
                      }, 100);
                    } else {
                      router(newPath);
                    }
                  }}
                  name="searchpallet"
                >
                  <div className="relative">
                    <MagnifyingGlassIcon
                      className="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                    <Combobox.Input
                      className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                      placeholder="Pesquisar..."
                      onChange={(event) => setRawQuery(event.target.value)}
                      value={rawQuery}
                    />
                    {loading && (
                      <div className="absolute right-4 top-3.5">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                      </div>
                    )}
                  </div>

                  {(filteredProjects.length > 0 ||
                    filteredUsers.length > 0 ||
                    filteredPosts.length > 0) && (
                    <Combobox.Options
                      static
                      className="max-h-80 scroll-py-10 scroll-pb-2 space-y-4 overflow-y-auto p-1 pb-2"
                    >
                      {filteredPosts.length > 0 && (
                        <li>
                          <h2 className="text-xs font-semibold text-gray-900">
                            Posts
                          </h2>
                          <ul className="-mx-4 mt-2 text-sm text-gray-700">
                            {filteredPosts.map((post: any) => (
                              <Combobox.Option
                                key={post.id}
                                value={post}
                                className={({ active }) =>
                                  classNames(
                                    "flex select-none items-center px-4 py-2",
                                    active && "bg-indigo-600 text-white"
                                  )
                                }
                              >
                                {({ active }) => (
                                  <>
                                    <MagnifyingGlassIcon
                                      className={classNames(
                                        "h-6 w-6 flex-none",
                                        active ? "text-white" : "text-gray-400"
                                      )}
                                      aria-hidden="true"
                                    />
                                    <div className="ml-3 flex-auto">
                                      <div className={classNames(
                                        "truncate",
                                        active ? "text-white" : "text-gray-900"
                                      )}>
                                        {post.title}
                                      </div>
                                      {post.subtitle && (
                                        <div className={classNames(
                                          "truncate text-xs",
                                          active ? "text-indigo-200" : "text-gray-500"
                                        )}>
                                          {post.subtitle}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </ul>
                        </li>
                      )}

                      {filteredUsers.length > 0 && (
                        <li>
                          <h2 className="text-xs font-semibold text-gray-900">
                            Contatos
                          </h2>
                          <ul className="-mx-4 mt-2 text-sm text-gray-700">
                            {filteredUsers.map((user: any) => (
                              <Combobox.Option
                                key={user.id}
                                value={user}
                                className={({ active }) =>
                                  classNames(
                                    "flex select-none items-center px-4 py-2",
                                    active && "bg-indigo-600 text-white"
                                  )
                                }
                              >
                                <Image
                                  src={user.avatar}
                                  alt="author"
                                  className="h-6 w-6 flex-none rounded-full"
                                  sizes="30px"
                                />
                                <span className="ml-3 flex-auto truncate">
                                  {user.displayName}
                                </span>
                              </Combobox.Option>
                            ))}
                          </ul>
                        </li>
                      )}
                    </Combobox.Options>
                  )}

                  {rawQuery === "?" && (
                    <div className="py-14 px-6 text-center text-sm sm:px-14">
                      <LifebuoyIcon
                        className="mx-auto h-6 w-6 text-gray-400"
                        aria-hidden="true"
                      />
                      <p className="mt-4 font-semibold text-gray-900">
                        Ajuda com a pesquisa
                      </p>
                      <p className="mt-2 text-gray-500">
                        Use esta ferramenta para pesquisar rapidamente por usuários e projetos em toda a nossa plataforma.
                      </p>
                    </div>
                  )}

                  {loading && (
                    <div className="py-14 px-6 text-center text-sm sm:px-14">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto"></div>
                      <p className="mt-4 font-semibold text-gray-900">
                        Pesquisando...
                      </p>
                    </div>
                  )}

                  {
                    !loading && (filteredPosts.length == 0 && filteredUsers.length == 0) && rawQuery !== "?" && (
                      rawQuery.trim() === "" ? (
                        <div className="py-14 px-6 text-center text-sm sm:px-14">
                          <MagnifyingGlassIcon
                            className="mx-auto h-6 w-6 text-gray-400"
                            aria-hidden="true"
                          />
                          <p className="mt-4 font-semibold text-gray-900">
                            Nenhuma informação pesquisada
                          </p>
                          <p className="mt-2 text-gray-500">
                            Informe no campo pesquisar o que você deseja encontrar.
                          </p>
                        </div>
                      ) : (
                        <div className="py-14 px-6 text-center text-sm sm:px-14">
                          <ExclamationTriangleIcon
                            className="mx-auto h-6 w-6 text-gray-400"
                            aria-hidden="true"
                          />
                          <p className="mt-4 font-semibold text-gray-900">
                            Não foram encontrados resultados
                          </p>
                          <p className="mt-2 text-gray-500">
                            Nós não encontramos nada com esse termo. Por favor, tente novamente.
                          </p>
                        </div>
                      )
                    )
                  }

                  <div className="flex flex-wrap items-center bg-gray-50 py-2.5 px-4 text-xs text-gray-700">
                  </div>
                </Combobox>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default SearchModal;
